import { hasOutstandingInvoiceCredit } from './invoiceCreditDiscrepancy'
import { formatInvoiceTotalDisplay } from './invoiceUploadHelpers'
import { formatTolerancePounds } from './invoiceMatchToleranceHelpers'
import { isInvoiceInReconciliationAlertWindow } from './reconciliationAlertCutoff'

export const ONE_CLICK_MATCHING_STATUS = 'match_found'
export const PARTIAL_MATCHING_STATUS = 'partial_match'
export const OUTSIDE_TOLERANCE_FAILURE = 'total_outside_tolerance'

function hasRecommendedPo(invoice = {}) {
  if (String(invoice.purchaseOrderNumber || '').trim()) return true
  if (String(invoice.suggestedPurchaseOrderNumber || '').trim()) return true
  if (String(invoice.matchSummary?.purchaseOrderNumber || '').trim())
    return true
  if (
    Array.isArray(invoice.matchedPurchaseOrderNumbers) &&
    invoice.matchedPurchaseOrderNumbers.some(Boolean)
  ) {
    return true
  }
  if (
    Array.isArray(invoice.matchedOrderIds) &&
    invoice.matchedOrderIds.some(Boolean)
  ) {
    return true
  }
  return Boolean(invoice.linkedOrderId)
}

/**
 * Whether the invoices table should show a Reconcile (why this match) CTA.
 */
export function canShowWhyThisMatchConfirm(invoice = {}) {
  if (hasOutstandingInvoiceCredit(invoice)) return false
  const lifecycle = invoice.status || 'pending'
  // Attached ≠ closed: allow one-click after Link attach until reconciled.
  if (lifecycle === 'reconciled' || lifecycle === 'sentToXero') {
    return false
  }

  // Explicit false wins — do not offer reconcile when only ordered would fit.
  if (invoice.matchSummary?.oneClickEligible === false) {
    return false
  }

  return (
    Boolean(invoice.matchSummary?.oneClickEligible) ||
    invoice.matchingStatus === ONE_CLICK_MATCHING_STATUS
  )
}

/**
 * Whether Force reconcile is available (outside tolerance / partial, with a PO).
 * Deliberate override only — UI must show the outside-tolerance warning.
 */
export function canShowForceReconcile(invoice = {}) {
  if (hasOutstandingInvoiceCredit(invoice)) return false
  const lifecycle = invoice.status || 'pending'
  if (lifecycle === 'reconciled' || lifecycle === 'sentToXero') {
    return false
  }
  if (!hasRecommendedPo(invoice)) {
    return false
  }

  if (invoice.matchingStatus === PARTIAL_MATCHING_STATUS) {
    return true
  }

  return (
    invoice.matchingStatus === 'needs_action' &&
    invoice.matchingFailureReason === OUTSIDE_TOLERANCE_FAILURE
  )
}

/** Count invoices that can show the one-click Reconcile CTA (alert window only). */
export function countReadyToReconcileInvoices(invoices = []) {
  return (invoices || []).filter(
    invoice =>
      isInvoiceInReconciliationAlertWindow(invoice) &&
      canShowWhyThisMatchConfirm(invoice),
  ).length
}

/** Whether a reconcile API error means the invoice is already done (safe no-op). */
export function isAlreadyReconciledError(error) {
  const status = error?.response?.status || error?.statusCode
  const message = String(
    error?.response?.data?.message || error?.message || '',
  ).toLowerCase()
  if (status === 409 && message.includes('already reconciled')) {
    return true
  }
  return message.includes('already reconciled or sent to xero')
}

/**
 * Invoice is soft-linked to 2+ POs without an Allocate plan — cannot one-click.
 */
export function isInvoiceLinkedToMultipleOrdersError(error) {
  const status = error?.response?.status || error?.statusCode
  if (status !== 409) return false
  const code =
    error?.response?.data?.code ||
    error?.response?.data?.data?.code ||
    error?.code ||
    error?.data?.code
  if (code === 'INVOICE_LINKED_TO_MULTIPLE_ORDERS') return true
  const message = String(
    error?.response?.data?.message || error?.message || '',
  ).toLowerCase()
  return (
    message.includes('linked to') &&
    message.includes('remove it from the extra')
  )
}

export function getInvoiceLinkedToMultipleOrdersMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    'This invoice is linked to more than one purchase order. Remove it from the extra orders (Remove from this PO) so it is linked to only one order, then try Match & reconcile again.'
  )
}

/** 409 because leftover GR lines are already receipted (shortage / credit). */
export function isPartialReceiptingConflictError(error) {
  const data = error?.response?.data?.data || error?.data || {}
  if (data.receiptingPartiallyComplete === true) return true
  const message = String(
    error?.response?.data?.message || error?.message || '',
  ).toLowerCase()
  return (
    message.includes('already receipted') &&
    message.includes('goods receipting')
  )
}

export function getMatchAndReconcileFailureTitle(error) {
  const processingError =
    error?.response?.data?.data?.processingError === true ||
    error?.data?.processingError === true
  if (processingError) return 'Reconciliation failed'
  if (isPartialReceiptingConflictError(error)) {
    return 'Goods Receipting has leftover lines'
  }
  return 'Could not match & reconcile'
}

/** Friendly copy when axios only returns a bare Network Error. */
export function getReconcileFailureDescription(error, { fallback } = {}) {
  const apiMessage = error?.response?.data?.message
  if (apiMessage) return apiMessage
  const raw = String(error?.message || '').trim()
  if (!raw || /^network error$/i.test(raw)) {
    return (
      fallback ||
      'We could not reach the server to finish reconciling. Check your connection and try again. If this invoice is linked to more than one order, remove it from the extras first.'
    )
  }
  return raw
}

/** Whether a 409 means the user must explicitly confirm force reconcile. */
export function isForceReconcileRequiredError(error) {
  const status = error?.response?.status || error?.statusCode
  if (status !== 409) return false
  const data = error?.response?.data?.data || error?.data || {}
  if (data.forceRequired === true && data.forceEligible === true) {
    return true
  }
  const message = String(
    error?.response?.data?.message || error?.message || '',
  ).toLowerCase()
  return message.includes('confirm force reconcile')
}

function toPenniesOrNull(value) {
  if (value == null || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

/** Label for the order-side total — received when GR adjusted, else ordered. */
export function getOrderGrossBasisLabel(basis) {
  if (basis === 'receipted') return 'Received total'
  if (basis === 'ordered') return 'Ordered total'
  return 'Order total'
}

export function getOutsideTolerancePenniesFromSummary(summary = {}) {
  const tolerance = toPenniesOrNull(summary.tolerancePennies) ?? 0
  const remaining = toPenniesOrNull(summary.remainingBalancePennies)
  if (remaining != null) {
    return Math.max(0, Math.abs(remaining) - tolerance)
  }
  const difference = toPenniesOrNull(summary.differencePennies)
  if (difference == null) return null
  return Math.max(0, Math.abs(difference) - tolerance)
}

/**
 * View-model for the “Why this match?” confirm modal.
 */
export function buildWhyThisMatchViewModel(invoice = {}) {
  const summary = invoice.matchSummary || {}
  const purchaseOrderNumber =
    summary.purchaseOrderNumber ||
    invoice.suggestedPurchaseOrderNumber ||
    (Array.isArray(invoice.matchedPurchaseOrderNumbers)
      ? invoice.matchedPurchaseOrderNumbers[0]
      : '') ||
    invoice.purchaseOrderNumber ||
    ''

  const invoiceGrossPennies =
    toPenniesOrNull(summary.invoiceGrossPennies) ??
    toPenniesOrNull(invoice.invoiceTotal)
  const orderGrossPennies = toPenniesOrNull(summary.orderGrossPennies)
  const orderGrossBasis =
    summary.orderGrossBasis === 'receipted' ||
    summary.orderGrossBasis === 'ordered'
      ? summary.orderGrossBasis
      : null
  const differencePennies = toPenniesOrNull(summary.differencePennies)
  const linkedInvoiceTotalPennies = toPenniesOrNull(
    summary.linkedInvoiceTotalPennies,
  )
  const remainingBalancePennies = toPenniesOrNull(
    summary.remainingBalancePennies,
  )
  const tolerancePennies = toPenniesOrNull(summary.tolerancePennies)
  const outsideTolerancePennies = getOutsideTolerancePenniesFromSummary(summary)

  const withinTolerance =
    differencePennies != null &&
    tolerancePennies != null &&
    differencePennies <= tolerancePennies

  const forceReconcile = canShowForceReconcile(invoice)
  // Explicit false wins (ordered-only fit must not unlock Confirm & reconcile).
  const oneClickEligible =
    hasOutstandingInvoiceCredit(invoice) || summary.oneClickEligible === false
      ? false
      : Boolean(
          summary.oneClickEligible ||
            invoice.matchingStatus === ONE_CLICK_MATCHING_STATUS,
        )

  return {
    purchaseOrderNumber: String(purchaseOrderNumber || '').trim(),
    supplierTitle: String(invoice.supplierTitle || '').trim(),
    invoiceNumber: String(invoice.invoiceNumber || '').trim(),
    invoiceGrossPennies,
    orderGrossPennies,
    orderGrossBasis,
    orderGrossLabel: getOrderGrossBasisLabel(orderGrossBasis),
    catchWeightPriceLabel: String(summary.catchWeightPriceLabel || '').trim(),
    differencePennies,
    linkedInvoiceTotalPennies,
    remainingBalancePennies,
    outsideTolerancePennies,
    tolerancePennies,
    invoiceGrossDisplay: formatInvoiceTotalDisplay(invoiceGrossPennies),
    orderGrossDisplay: formatInvoiceTotalDisplay(orderGrossPennies),
    differenceDisplay: formatInvoiceTotalDisplay(differencePennies),
    linkedInvoiceTotalDisplay: formatInvoiceTotalDisplay(
      linkedInvoiceTotalPennies,
    ),
    remainingBalanceDisplay: formatInvoiceTotalDisplay(remainingBalancePennies),
    outsideToleranceDisplay: formatInvoiceTotalDisplay(outsideTolerancePennies),
    toleranceDisplay:
      tolerancePennies == null ? '—' : formatTolerancePounds(tolerancePennies),
    withinTolerance,
    oneClickEligible,
    forceReconcile,
    matchingStatus: invoice.matchingStatus || null,
    matchingReasonDetail: String(invoice.matchingReasonDetail || '').trim(),
    matchedOrderIds: Array.isArray(invoice.matchedOrderIds)
      ? invoice.matchedOrderIds.map(id => String(id))
      : [],
    warningTitle: forceReconcile
      ? `Outside tolerance by ${formatInvoiceTotalDisplay(
          outsideTolerancePennies,
        )}`
      : null,
    warningDetail: forceReconcile
      ? `Totals differ beyond your ${
          tolerancePennies == null
            ? 'tolerance'
            : formatTolerancePounds(tolerancePennies)
        } tolerance. Linking still works — finish reconciliation on the Goods Receipting page if you need to close the order despite the difference.`
      : null,
  }
}
